import React from 'react';
import {
  Box,
  IconButton,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import LocationModal from './LocationModal';
import LocationDropdown from './LocationDropdown';
import FilterDropdown from './FilterDropdown';
import SearchDropdown from './SearchDropdown';
import CustomDropdown from './CustomDropdown';

import {
  SearchContainer,
  SearchInput,
  SearchButton,
} from './styles';

import { DEFAULT_PRICE_RANGES, DEFAULT_AREA_RANGES } from './constants/searchConstants';

import { useApiData } from './hooks/useApiData';
import { useSearchBarState } from './hooks/useSearchBarState';
import { createSearchHandlers } from './handlers/searchHandlers';
import { createModalHandlers } from './handlers/modalHandlers';
import { segmentsToDisplayString } from './utils/segmentHelpers';
import { UrlService } from './services/urlService';

export { default as FilterDropdown } from './FilterDropdown';
export { default as LocationModal } from './LocationModal';
export { default as LocationDropdown } from './LocationDropdown';
export { default as SearchDropdown } from './SearchDropdown';
export { default as LocationPopover } from './LocationPopover';
export { default as CustomDropdown } from './CustomDropdown';

export { FiltersService } from './services/filtersService';
export type {
  FilterResponse,
  FilterErrorResponse,
  FilterData,
  FilterMeta,
  ActionInfo,
  CategoryInfo,
  FilterGroup,
  FilterOption,
  ResponseMeta,
  FiltersApiResponse
} from './services/filtersService';

export { useFilters, useActionCategoryIds, useFiltersWithNames } from './hooks/useFilters';

export * from './utils';
import { getCategoryNameByLocale } from './utils';


const initialLocationCategories = [
  'location_categories.province',
  'location_categories.project_name',
  'location_categories.mass_transit',
  'location_categories.shopping_mall',
  'location_categories.university',
  'location_categories.road',
  'location_categories.hospital',
  'location_categories.park',
  'location_categories.airport',
  'location_categories.popular_location',
];

interface CategoryItem {
  category_ID: string;
  category_Name_TH: string;
  category_Name_EN: string;
  category_Name_CN: string;
  category_Desc: string | null;
  category_Status: string;
  category_Sort: string;
  type: string;
}

interface ActionItem {
  id: string;
  action: string;
}

interface SearchBarProps {
  categories?: CategoryItem[];
  actions?: ActionItem[];
}

const SearchBar: React.FC<SearchBarProps> = ({ categories, actions }) => {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language || 'th';
  const router = useRouter();

  // Track if URL has been parsed to prevent re-parsing on state changes
  const hasInitializedFromUrl = useRef(false);

  // ตรวจสอบว่ามีข้อมูล static data ครบแล้วหรือไม่
  const hasStaticData = categories && categories.length > 0 && actions && actions.length > 0;

  // ถ้ามีข้อมูล static data ครบแล้ว ไม่ต้องเรียก useApiData
  const apiData = useApiData(locale, hasStaticData ? undefined : { categories, actions });
  
  // ใช้ข้อมูลจาก static data หรือ API data
  const filteredActions = hasStaticData ? actions : apiData.filteredActions;
  const filteredCategories = hasStaticData ? categories : apiData.filteredCategories;

  const state = useSearchBarState(t);

  // Location categories for dropdown
  const locationCategories = initialLocationCategories;

  // Create search handlers with proper props
  const searchHandlers = createSearchHandlers({
    // State setters
    setSearchText: state.setSearchText,
    setTransactionType: state.setTransactionType,
    setPropertyType: state.setPropertyType,
    setSelectedLocation: state.setSelectedLocation,
    setSelectedSubLocation: state.setSelectedSubLocation,
    setIsSearchDisabled: state.setIsSearchDisabled,
    setShowDropdown: state.setShowDropdown,
    setShowLocationDropdown: state.setShowLocationDropdown,
    setModalSearchValue: state.setModalSearchValue,
    setIsSearching: state.setIsSearching,
    setSearchResults: state.setSearchResults,
    setHasMore: state.setHasMore,
    setPage: state.setPage,
    setIsLoadingMore: state.setIsLoadingMore,
    setOpenModal: state.setOpenModal,
    setLocationTypeId: state.setLocationTypeId,
    setLocationData: state.setLocationData,
    setLoadingLocationData: state.setLoadingLocationData,
    setLocationSearchText: state.setLocationSearchText,
    setNavigationHistory: state.setNavigationHistory,
    setModalView: state.setModalView,
    setSelectedMainOption: state.setSelectedMainOption,

    // State values
    searchText: state.searchText,
    transactionType: state.transactionType,
    propertyType: state.propertyType,
    priceMin: state.priceMin,
    priceMax: state.priceMax,
    selectedLocation: state.selectedLocation,
    selectedSubLocation: state.selectedSubLocation,
    searchResults: state.searchResults,
    page: state.page,
    hasMore: state.hasMore,
    isSearching: state.isSearching,
    isLoadingMore: state.isLoadingMore,
    locationTypeId: state.locationTypeId,
    locationData: state.locationData,
    modalSearchValue: state.modalSearchValue,
    selectedMainOption: state.selectedMainOption,
    navigationHistory: state.navigationHistory,
    searchDebounceTimeout: state.searchDebounceTimeout,

    // Optional state
    selectedBedrooms: state.selectedBedrooms,
    selectedBathrooms: state.selectedBathrooms,
    selectedQuota: state.selectedQuota,
    selectedStatus: state.selectedStatus,
    selectedFloor: state.selectedFloor,
    selectedView: state.selectedView,
    selectedDirection: state.selectedDirection,
    selectedPublishedBy: state.selectedPublishedBy,
    selectedPostedDate: state.selectedPostedDate,
    areaMin: state.areaMin,
    areaMax: state.areaMax,
    selectedDynamicFilters: state.selectedDynamicFilters,

    // new segment props
    locationSegments: state.locationSegments,
    pushSegment: state.pushSegment,
    popSegment: state.popSegment,
    replaceLastSegmentLabel: state.replaceLastSegmentLabel,
    setLocationSegments: state.setLocationSegments,

    // API data
    filteredActions,
    actionMapping: {},
    filteredCategories,
    categoryMapping: {},

    // Locale and translation
    locale,
    t,
    
    // เพิ่ม router
    router
  });

  // Handler functions using proper search handlers
  const handleTransactionTypeChange = searchHandlers.handleTransactionTypeChange;
  const handlePropertyTypeChange = searchHandlers.handlePropertyTypeChange;
  const handleSearchTextChange = searchHandlers.handleSearchTextChange;
  const handleClearSearch = searchHandlers.handleClearSearch;
  const handleSearchInputClick = searchHandlers.handleSearchInputClick;
  const handleLocationTypeClick = searchHandlers.handleLocationTypeClick;
  const handleSearch = searchHandlers.handleSearch;

  // Create modal handlers
  const modalHandlers = createModalHandlers({
    setOpenModal: state.setOpenModal,
    setNavigationHistory: state.setNavigationHistory,
    setSelectedMainOption: state.setSelectedMainOption,
    setModalView: state.setModalView,
    setModalSearchValue: state.setModalSearchValue,
    setLoadingLocationData: state.setLoadingLocationData,
    setIsLoadingMore: state.setIsLoadingMore,
    setLocationData: state.setLocationData,
    setHasMore: state.setHasMore,
    setPage: state.setPage,
    setSelectedLocation: state.setSelectedLocation,
    setSelectedSubLocation: state.setSelectedSubLocation,
    setSearchText: state.setSearchText,
    setShowDropdown: state.setShowDropdown,

    navigationHistory: state.navigationHistory,
    selectedMainOption: state.selectedMainOption,
    modalView: state.modalView,
    modalSearchValue: state.modalSearchValue,
    locationTypeId: state.locationTypeId,
    transactionType: state.transactionType,
    propertyType: state.propertyType,
    locationData: state.locationData,
    page: state.page,
    hasMore: state.hasMore,
    loadingLocationData: state.loadingLocationData,
    isLoadingMore: state.isLoadingMore,

    getActionIdFromType: searchHandlers.getActionIdFromType,
    getCategoryIdFromType: searchHandlers.getCategoryIdFromType,
    loadLocationDataBySearch: searchHandlers.loadLocationDataBySearch,
    
    locale
  });

  const handleMinInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/[^0-9]/g, '');
    const numericValue = Number(rawValue) || 0;
    
    // ตรวจสอบว่าราคาต่ำสุดไม่เกินราคาสูงสุด
    const currentMaxPrice = state.priceMax;
    if (currentMaxPrice > 0 && numericValue > currentMaxPrice) {
      return; // ไม่อัปเดตถ้าราคาต่ำสุดมากกว่าราคาสูงสุด
    }
    
    const formattedValue = rawValue ? new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 0,
    }).format(numericValue) : '';
    
    state.setMinInputValue(formattedValue);
    state.setPriceMin(numericValue);
  };

  const handleMaxInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/[^0-9]/g, '');
    const numericValue = Number(rawValue) || 0;
    
    // ตรวจสอบว่าราคาสูงสุดไม่น้อยกว่าราคาต่ำสุด
    const currentMinPrice = state.priceMin;
    if (numericValue > 0 && currentMinPrice > 0 && numericValue < currentMinPrice) {
      // ตั้งราคาสูงสุด = ราคาต่ำสุด และแสดงข้อความเตือน
      const adjustedValue = currentMinPrice;
      const formattedValue = new Intl.NumberFormat('th-TH', {
        maximumFractionDigits: 0,
      }).format(adjustedValue);
      
      state.setMaxInputValue(formattedValue);
      state.setPriceMax(adjustedValue);
      state.setPriceErrorMessage(t('price_validation_error'));
      return;
    }
    
    // ล้างข้อความแจ้งเตือนถ้าราคาถูกต้อง
    state.setPriceErrorMessage('');
    
    const formattedValue = rawValue ? new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 0,
    }).format(numericValue) : '';
    
    state.setMaxInputValue(formattedValue);
    state.setPriceMax(numericValue);
  };

  const handleMinPriceSelect = (price: number) => {
    // ตรวจสอบว่าราคาต่ำสุดไม่เกินราคาสูงสุด
    const currentMaxPrice = state.priceMax;
    if (currentMaxPrice > 0 && price > currentMaxPrice) {
      return; // ไม่อัปเดตถ้าราคาต่ำสุดมากกว่าราคาสูงสุด
    }
    
    state.setPriceMin(price);
    // Format และอัปเดต input value
    const formattedValue = price === 0 ? '' : new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 0,
    }).format(price);
    state.setMinInputValue(formattedValue);
  };

  const handleMaxPriceSelect = (price: number) => {
    // ตรวจสอบว่าราคาสูงสุดไม่น้อยกว่าราคาต่ำสุด
    const currentMinPrice = state.priceMin;
    if (price > 0 && currentMinPrice > 0 && price < currentMinPrice) {
      // ตั้งราคาสูงสุด = ราคาต่ำสุด และแสดงข้อความเตือน
      const adjustedValue = currentMinPrice;
      const formattedValue = new Intl.NumberFormat('th-TH', {
        maximumFractionDigits: 0,
      }).format(adjustedValue);
      
      state.setMaxInputValue(formattedValue);
      state.setPriceMax(adjustedValue);
      state.setPriceErrorMessage(t('price_validation_error'));
      return;
    }
    
    // ล้างข้อความแจ้งเตือนถ้าราคาถูกต้อง
    state.setPriceErrorMessage('');
    
    state.setPriceMax(price);
    // Format และอัปเดต input value
    const formattedValue = new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 0,
    }).format(price);
    state.setMaxInputValue(formattedValue);
  };

  const handleAreaMinInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/[^0-9]/g, '');
    const formattedValue = rawValue ? new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 0,
    }).format(Number(rawValue)) : '';
    
    state.setAreaMinInput(formattedValue);
    state.setAreaMin(Number(rawValue) || 0);
  };

  const handleAreaMaxInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/[^0-9]/g, '');
    const formattedValue = rawValue ? new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 0,
    }).format(Number(rawValue)) : '';
    
    state.setAreaMaxInput(formattedValue);
    state.setAreaMax(Number(rawValue) || 0);
  };

  const handleAreaMinSelect = (area: number) => {
    state.setAreaMin(area);
    // Format และอัปเดต area input value
    const formattedValue = area === 0 ? '' : new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 0,
    }).format(area);
    state.setAreaMinInput(formattedValue);
  };

  const handleAreaMaxSelect = (area: number) => {
    state.setAreaMax(area);
    // Format และอัปเดต area input value
    const formattedValue = new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 0,
    }).format(area);
    state.setAreaMaxInput(formattedValue);
  };

  const handleFilterApply = () => {
    console.log('Filter applied');
  };

  const handleFilterReset = () => {
    state.setPriceMin(0);
    state.setPriceMax(0);
    state.setMinInputValue('');
    state.setMaxInputValue('');
    state.setAreaMin(0);
    state.setAreaMax(1000);
    state.setAreaMinInput('0');
    state.setAreaMaxInput('1,000');
    state.setSelectedBedrooms(null);
    state.setSelectedBathrooms(null);
  };

  // Clear handlers for input fields
  const handleMinInputClear = () => {
    state.setPriceMin(0);
    state.setMinInputValue('');
  };

  const handleMaxInputClear = () => {
    state.setPriceMax(0);
    state.setMaxInputValue('');
  };

  const handleAreaMinInputClear = () => {
    state.setAreaMin(0);
    state.setAreaMinInput('');
  };

  const handleAreaMaxInputClear = () => {
    state.setAreaMax(0);
    state.setAreaMaxInput('');
  };

  // local clear click to ensure immediate state reset without stale closure issues
  const onSearchClearClick = () => {
    console.log('SearchBarHome: Clear button clicked', {
      currentSearchText: state.searchText,
      currentSegments: state.locationSegments
    });
    searchHandlers.handleClearSearch();
    state.setLocationSegments([]); // safeguard
    if (state.searchInputRef.current) {
      (state.searchInputRef.current as HTMLInputElement).value = '';
    }
    setTimeout(() => {
      console.log('SearchBarHome: state after clear', {
        searchText: state.searchText,
        segments: state.locationSegments
      });
    }, 0);
  };

  // Initialize state from URL parameters when component mounts (only once)
  useEffect(() => {
    const initializeFromUrl = () => {
      // หากยังไม่มีข้อมูล action/category เพียงพอ ให้รอจนกว่าจะโหลดเสร็จ
      if (
        hasInitializedFromUrl.current ||
        typeof window === 'undefined' ||
        router.pathname === '/' ||
        !filteredActions || filteredActions.length === 0 ||
        !filteredCategories || filteredCategories.length === 0
      ) {
        return;
      }

      try {
        const currentUrl = window.location.href;
        const parsedData = UrlService.parseUrlToSearchData(
          currentUrl,
          filteredActions,
          filteredCategories,
          locale,
          t
        );

        // Update state based on parsed URL data
        if (parsedData.transactionType) {
          state.setTransactionType(parsedData.transactionType);
        }
        if (parsedData.propertyType) {
          state.setPropertyType(parsedData.propertyType);
        }
        if (parsedData.searchText) {
          state.setSearchText(parsedData.searchText);
        }
        if (parsedData.locationSegments && parsedData.locationSegments.length > 0) {
          state.setLocationSegments(parsedData.locationSegments);
        }
        if (parsedData.priceMin !== undefined) {
          state.setPriceMin(parsedData.priceMin);
          state.setMinInputValue(parsedData.priceMin.toString());
        }
        if (parsedData.priceMax !== undefined) {
          state.setPriceMax(parsedData.priceMax);
          state.setMaxInputValue(parsedData.priceMax.toString());
        }
        if (parsedData.areaMin !== undefined) {
          state.setAreaMin(parsedData.areaMin);
          state.setAreaMinInput(parsedData.areaMin.toString());
        }
        if (parsedData.areaMax !== undefined) {
          state.setAreaMax(parsedData.areaMax);
          state.setAreaMaxInput(parsedData.areaMax.toString());
        }
        if (parsedData.selectedBedrooms) {
          state.setSelectedBedrooms(parsedData.selectedBedrooms);
        }
        if (parsedData.selectedBathrooms) {
          state.setSelectedBathrooms(parsedData.selectedBathrooms);
        }
        if (parsedData.selectedQuota) {
          state.setSelectedQuota(parsedData.selectedQuota);
        }
        if (parsedData.selectedStatus) {
          state.setSelectedStatus(parsedData.selectedStatus);
        }
        if (parsedData.selectedFloor) {
          state.setSelectedFloor(parsedData.selectedFloor);
        }
        if (parsedData.selectedView) {
          state.setSelectedView(parsedData.selectedView);
        }
        if (parsedData.selectedDirection) {
          state.setSelectedDirection(parsedData.selectedDirection);
        }
        if (parsedData.selectedPublishedBy) {
          state.setSelectedPublishedBy(parsedData.selectedPublishedBy);
        }
        if (parsedData.selectedPostedDate) {
          state.setSelectedPostedDate(parsedData.selectedPostedDate);
        }
        if (parsedData.selectedDynamicFilters) {
          state.setSelectedDynamicFilters(parsedData.selectedDynamicFilters);
        }

        // Mark as initialized to prevent re-parsing
        hasInitializedFromUrl.current = true;

        console.log('SearchBarHome: Initialized from URL', {
          currentUrl,
          parsedData,
          locationSegments: parsedData.locationSegments
        });
      } catch (error) {
        console.error('SearchBarHome: Error parsing URL', error);
      }
    };

    // Delay initialization to ensure all data is loaded
    const timeoutId = setTimeout(initializeFromUrl, 100);
    return () => clearTimeout(timeoutId);
  }, [filteredActions, filteredCategories, locale, router.pathname, t]);

  return (
    <SearchContainer>
      {/* Transaction Type Dropdown */}
      <CustomDropdown
        options={filteredActions.map(action => ({
          id: action.id,
          value: action.action,
          label: action.action
        }))}
        value={state.transactionType}
        onChange={handleTransactionTypeChange}
        placeholder={t('transaction_type')}
      />

      {/* Property Type Dropdown */}
      <CustomDropdown
        options={filteredCategories.map(category => {
          const categoryName = getCategoryNameByLocale(category, locale);
          return {
            id: category.category_ID,
            value: categoryName,
            label: categoryName
          };
        })}
        value={state.propertyType}
        onChange={handlePropertyTypeChange}
        placeholder={t('property_type')}
        firstOption={t('estate')}
      />

      {/* Search Input */}
      <Box sx={{ position: 'relative', flex: 1 }}>
        <SearchInput
          ref={state.searchInputRef}
          placeholder={t('search_placeholder')}
          value={state.locationSegments && state.locationSegments.length > 0 ?
            segmentsToDisplayString(state.locationSegments, locale) : state.searchText}
          onChange={handleSearchTextChange}
          onClick={handleSearchInputClick}
          endAdornment={(state.searchText || (state.locationSegments && state.locationSegments.length > 0)) && (
            <InputAdornment position="end">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onSearchClearClick();
                }}
                edge="end"
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )}
        />

        {/* Search Dropdown */}
        <SearchDropdown
          isVisible={state.showDropdown}
          items={state.searchResults}
          loading={state.isSearching}
          searchText={state.searchText}
          onItemClick={searchHandlers.handleDropdownItemClick}
          anchorEl={state.searchInputRef.current}
          hasMore={state.hasMore}
          onScroll={searchHandlers.handleDropdownScroll}
          onClose={() => state.setShowDropdown(false)}
        />

        {/* Location Dropdown */}
        <LocationDropdown
          isVisible={state.showLocationDropdown}
          locationCategories={locationCategories}
          onLocationClick={handleLocationTypeClick}
          anchorEl={state.searchInputRef.current}
          onClose={() => state.setShowLocationDropdown(false)}
        />
      </Box>

      {/* Filter Dropdown */}
      <Box>
        <FilterDropdown
        priceMin={state.priceMin}
        priceMax={state.priceMax}
        minInputValue={state.minInputValue}
        maxInputValue={state.maxInputValue}
        priceErrorMessage={state.priceErrorMessage}
        priceRangesMin={DEFAULT_PRICE_RANGES.MIN}
        priceRangesMax={DEFAULT_PRICE_RANGES.MAX}
        onMinInputChange={handleMinInputChange}
        onMaxInputChange={handleMaxInputChange}
        onMinPriceSelect={handleMinPriceSelect}
        onMaxPriceSelect={handleMaxPriceSelect}
        onMinInputClear={handleMinInputClear}
        onMaxInputClear={handleMaxInputClear}
        selectedBedrooms={state.selectedBedrooms ? parseInt(state.selectedBedrooms) : null}
        onBedroomSelect={(bedrooms) => state.setSelectedBedrooms(bedrooms?.toString() || null)}
        selectedBathrooms={state.selectedBathrooms ? parseInt(state.selectedBathrooms) : null}
        onBathroomSelect={(bathrooms) => state.setSelectedBathrooms(bathrooms?.toString() || null)}
        areaMin={state.areaMin}
        areaMax={state.areaMax}
        areaMinInput={state.areaMinInput}
        areaMaxInput={state.areaMaxInput}
        areaRangesMin={DEFAULT_AREA_RANGES.MIN}
        areaRangesMax={DEFAULT_AREA_RANGES.MAX}
        onAreaMinInputChange={handleAreaMinInputChange}
        onAreaMaxInputChange={handleAreaMaxInputChange}
        onAreaMinSelect={handleAreaMinSelect}
        onAreaMaxSelect={handleAreaMaxSelect}
        onAreaMinInputClear={handleAreaMinInputClear}
        onAreaMaxInputClear={handleAreaMaxInputClear}
        selectedQuota={state.selectedQuota}
        onQuotaSelect={(quota) => state.setSelectedQuota(quota)}
        selectedStatus={state.selectedStatus}
        onStatusSelect={(status) => state.setSelectedStatus(status)}
        selectedFloor={state.selectedFloor}
        onFloorSelect={(floor) => state.setSelectedFloor(floor)}
        selectedView={state.selectedView}
        onViewSelect={(view) => state.setSelectedView(view)}
        selectedDirection={state.selectedDirection}
        onDirectionSelect={(direction) => state.setSelectedDirection(direction)}
        selectedPublishedBy={state.selectedPublishedBy}
        onPublishedBySelect={(publishedBy) => state.setSelectedPublishedBy(publishedBy)}
        selectedPostedDate={state.selectedPostedDate}
        onPostedDateSelect={(postedDate) => state.setSelectedPostedDate(postedDate)}
        selectedAction={state.transactionType}
        selectedCategory={state.propertyType}
        selectedLocation={state.selectedLocation?.label || ''}
        selectedSubLocation={state.selectedSubLocation?.label || ''}
        locationTypeId={state.locationTypeId}
        onDynamicFilterChange={(groupId, optionId, queryParams) => {
          state.setSelectedDynamicFilters(prev => ({
            ...prev,
            [groupId]: optionId || 0
          }));
        }}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        />
      </Box>

      {/* Search Button */}
      <SearchButton onClick={handleSearch}>
        {t('search')}
      </SearchButton>

      {/* Location Modal */}
      <LocationModal
        open={state.openModal}
        onClose={modalHandlers.handleModalClose}
        locationSearchText={state.locationSearchText}
        onSearchChange={modalHandlers.handleLocationSearch}
        loadingLocationData={state.loadingLocationData}
        locationData={state.locationData}
        modalView={state.modalView}
        selectedMainOption={state.selectedMainOption}
        onMainOptionClick={modalHandlers.handleMainOptionClick}
        onBackToMain={modalHandlers.handleBackToMain}
        onSelectSubLocation={modalHandlers.handleSelectSubLocation}
        onClearSearch={() => {
          state.setLocationSearchText('');
          state.setModalSearchValue('');
        }}
        onSaveSettings={modalHandlers.handleSaveSettings}
        isSearchDisabled={state.isSearchDisabled}
        searchValue={state.modalSearchValue}
        hasSubLocations={modalHandlers.hasSubLocations}
        onScroll={modalHandlers.handleScroll}
        isLoadingMore={state.isLoadingMore}
        onFinalSelect={modalHandlers.handleFinalSelect}
      />
    </SearchContainer>
  );
};

export default SearchBar; 